import { Plus, Trash2 } from "lucide-react";
import React from "react";
import { useFieldArray, UseFormReturn } from "react-hook-form";

import {
  Button,
  Card,
  DatePicker,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components";
import RichTextEditor from "@/components/rich-text-editor";
import { Checkbox } from "@/components/ui/checkbox";
import { NumericInput } from "@/components/ui/numeric-input";
import { RealCurrencyInput } from "@/components/ui/real-currency-input";
import { RequiredLabel } from "@/components/ui/required-label";
import { useI18n } from "@/context/i18n";
import { useInsuranceCompanies } from "@/pages/insurance-companies/insurance-companies.hooks";

import { TProductFormData } from "../../products.schema";
import { createEmptySoulhubAssistanceCapitalRange } from "../../schemas/assistance.schema";
import { SoulhubExternalProductIdField } from "../soulhub-external-product-id-field";
import { PlanAdjustmentsSection } from "./plan-adjustments-section/plan-adjustments-section";

export interface IInsuranceWarrantyFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  form: UseFormReturn<TProductFormData>;
  onSubmit: (values: TProductFormData) => void;
  isEditMode?: boolean;
  /** "warranty" = Seguro Garantia; "theft" = Roubo e Furto (mesmo layout, outro JSON). */
  productKind?: "warranty" | "theft";
  /**
   * Assistência SoulHub: mesma UI da Garantia na aba Dados; na aba Planos as faixas
   * (`capital_ranges`) substituem `plans` no JSON.
   */
  assistanceSoulhubMode?: boolean;
}

const COMM_RULES = [
  "age",
  "gender",
  "km",
  "location_state",
  "location_city",
  "vehicle_age",
  "vehicle_type",
  "model_or_brand_groups",
] as const;

export const InsuranceWarrantyForm: React.FC<IInsuranceWarrantyFormProps> = ({
  open,
  form,
  onSubmit,
  isEditMode,
  productKind = "warranty",
  assistanceSoulhubMode = false,
}) => {
  const { t } = useI18n();
  const cfgPrefix = assistanceSoulhubMode
    ? "assistance_product_config"
    : productKind === "theft"
      ? "theft_product_config"
      : "warranty_product_config";
  const tk = React.useCallback(
    (key: string) => {
      if (assistanceSoulhubMode) {
        return t(`products.warranty.${key}`);
      }
      return t(
        `products.${productKind === "theft" ? "theft" : "warranty"}.${key}`,
      );
    },
    [t, productKind, assistanceSoulhubMode],
  );
  const [activeTab, setActiveTab] = React.useState("data");
  const { insuranceCompanies } = useInsuranceCompanies();

  const emission = form.watch(`${cfgPrefix}.emission_type` as any);
  const collective = form.watch(`${cfgPrefix}.is_collective_policy` as any);
  const contractActive = form.watch(`${cfgPrefix}.contract_active` as any);
  const rules = form.watch(`${cfgPrefix}.commercialization_rules` as any) || [];
  const premiumType = form.watch(`${cfgPrefix}.premium_price_type` as any);
  const markupEnabled = form.watch(`${cfgPrefix}.markup_enabled` as any);
  const markupMode = form.watch(`${cfgPrefix}.markup_mode` as any);
  const proLaboreOn = form.watch(`${cfgPrefix}.pro_labore_enabled` as any);
  const proLaboreMode = form.watch(`${cfgPrefix}.pro_labore_value_mode` as any);
  const hasPlans = form.watch(`${cfgPrefix}.has_additional_plans` as any);
  const vehicleAgeRule = form.watch(`${cfgPrefix}.vehicle_age_rule` as any);

  const plansFieldName = assistanceSoulhubMode
    ? ("capital_ranges" as const)
    : (`${cfgPrefix}.plans` as const);

  const {
    fields: planFields,
    append: appendPlan,
    remove: removePlan,
  } = useFieldArray({
    control: form.control,
    name: plansFieldName as any,
  });

  React.useEffect(() => {
    if (open) {
      setActiveTab("data");
    }
  }, [open]);

  /** Assistência + preço NET: markup visível sem ligar o interruptor (mesmo critério esperado no schema). */
  React.useEffect(() => {
    if (!assistanceSoulhubMode) {
      return;
    }
    if (premiumType === "net") {
      form.setValue(`${cfgPrefix}.markup_enabled` as any, true);
      const mode = form.getValues(`${cfgPrefix}.markup_mode` as any);
      if (mode === undefined || mode === null || mode === "") {
        form.setValue(`${cfgPrefix}.markup_mode` as any, "percentage");
      }
    }
  }, [assistanceSoulhubMode, premiumType, cfgPrefix, form]);

  const showMarkupFields = assistanceSoulhubMode
    ? premiumType === "net" || markupEnabled === true
    : markupEnabled === true;

  const handleTabChange = React.useCallback((value: string) => {
    // Troca livre entre Dados e Planos: com "possui outros planos" o utilizador
    // precisa ir à aba Planos antes de existir plano válido; a validação completa
    // ocorre no submit (warrantyInsuranceSchema).
    setActiveTab(value);
  }, []);

  if (!open) {
    return null;
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit((data) => onSubmit(data))}
        className="space-y-4"
      >
        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsList>
            <TabsTrigger value="data">
              1. {tk("tab.data")}
            </TabsTrigger>
            <TabsTrigger value="plans">
              2. {tk("tab.plans")}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="data">
            <Card className="p-4 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <RequiredLabel required>
                        {t("products.form.fields.name")}
                      </RequiredLabel>
                      <FormControl>
                        <Input {...field} placeholder=" " />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <SoulhubExternalProductIdField form={form} />
                <FormField
                  control={form.control}
                  name="insurance_company_id"
                  render={({ field }) => (
                    <FormItem>
                      <RequiredLabel required>
                        {tk("fields.guarantor")}
                      </RequiredLabel>
                      <FormControl>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value || ""}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue
                              placeholder={t(
                                "products.form.fields.insurance_company_id.placeholder"
                              )}
                            />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectGroup>
                              {insuranceCompanies.map((c) => (
                                <SelectItem key={c.id} value={c.id || ""}>
                                  {c.name}
                                </SelectItem>
                              ))}
                            </SelectGroup>
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name={`${cfgPrefix}.emission_type` as any}
                  render={({ field }) => (
                    <FormItem>
                      <RequiredLabel required>
                        {tk("fields.emission_type")}
                      </RequiredLabel>
                      <FormControl>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value || ""}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder=" " />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="apolice">
                              {tk("emission.apolice")}
                            </SelectItem>
                            <SelectItem value="bilhete">
                              {tk("emission.bilhete")}
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {emission === "apolice" ? (
                  <FormField
                    control={form.control}
                    name={`${cfgPrefix}.is_collective_policy` as any}
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center gap-3 space-y-0">
                        <FormControl>
                          <Switch
                            checked={field.value === true}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <FormLabel>
                          {tk("fields.collective_policy")}
                        </FormLabel>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                ) : null}

                {emission === "apolice" && collective === true ? (
                  <>
                    <FormField
                      control={form.control}
                      name="policy_number"
                      render={({ field }) => (
                        <FormItem>
                          <RequiredLabel required>
                            {t("products.form.fields.policy_number")}
                          </RequiredLabel>
                          <FormControl>
                            <Input {...field} value={field.value || ""} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="policy_start_date"
                      render={({ field }) => (
                        <FormItem>
                          <RequiredLabel required>
                            {t("products.form.fields.policy_start_date")}
                          </RequiredLabel>
                          <FormControl>
                            <DatePicker
                              value={field.value as Date | undefined}
                              onChange={field.onChange}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="policy_end_date"
                      render={({ field }) => (
                        <FormItem>
                          <RequiredLabel required>
                            {t("products.form.fields.policy_end_date")}
                          </RequiredLabel>
                          <FormControl>
                            <DatePicker
                              value={field.value as Date | undefined}
                              onChange={field.onChange}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </>
                ) : null}

                {emission === "bilhete" ? (
                  assistanceSoulhubMode ? (
                    <div className="md:col-span-2 space-y-3">
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <p className="text-base font-semibold">
                          {tk("fields.contract_section_title")}
                        </p>
                        <FormField
                          control={form.control}
                          name={`${cfgPrefix}.contract_active` as any}
                          render={({ field }) => (
                            <FormItem className="flex flex-row items-center gap-3 space-y-0">
                              <FormControl>
                                <Switch
                                  checked={field.value === true}
                                  onCheckedChange={field.onChange}
                                  aria-label={tk("fields.contract_active")}
                                />
                              </FormControl>
                              <FormLabel className="sr-only">
                                {tk("fields.contract_active")}
                              </FormLabel>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                      {contractActive === true ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <FormField
                            control={form.control}
                            name={`${cfgPrefix}.contract_start_date` as any}
                            render={({ field }) => (
                              <FormItem>
                                <RequiredLabel required>
                                  {tk("fields.contract_start")}
                                </RequiredLabel>
                                <FormControl>
                                  <DatePicker
                                    value={field.value as Date | undefined}
                                    onChange={field.onChange}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name={`${cfgPrefix}.contract_end_date` as any}
                            render={({ field }) => (
                              <FormItem>
                                <RequiredLabel required>
                                  {tk("fields.contract_end")}
                                </RequiredLabel>
                                <FormControl>
                                  <DatePicker
                                    value={field.value as Date | undefined}
                                    onChange={field.onChange}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                      ) : null}
                    </div>
                  ) : (
                    <>
                      <FormField
                        control={form.control}
                        name={`${cfgPrefix}.contract_active` as any}
                        render={({ field }) => (
                          <FormItem className="flex flex-row items-center gap-3 space-y-0">
                            <FormControl>
                              <Switch
                                checked={field.value === true}
                                onCheckedChange={field.onChange}
                              />
                            </FormControl>
                            <FormLabel>
                              {tk("fields.contract_active")}
                            </FormLabel>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      {contractActive === true ? (
                        <>
                          <FormField
                            control={form.control}
                            name={`${cfgPrefix}.contract_start_date` as any}
                            render={({ field }) => (
                              <FormItem>
                                <RequiredLabel required>
                                  {tk("fields.contract_start")}
                                </RequiredLabel>
                                <FormControl>
                                  <DatePicker
                                    value={field.value as Date | undefined}
                                    onChange={field.onChange}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name={`${cfgPrefix}.contract_end_date` as any}
                            render={({ field }) => (
                              <FormItem>
                                <RequiredLabel required>
                                  {tk("fields.contract_end")}
                                </RequiredLabel>
                                <FormControl>
                                  <DatePicker
                                    value={field.value as Date | undefined}
                                    onChange={field.onChange}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </>
                      ) : null}
                    </>
                  )
                ) : null}

                <FormField
                  control={form.control}
                  name="type_of_vigency"
                  render={({ field }) => (
                    <FormItem>
                      <RequiredLabel required>
                        {assistanceSoulhubMode
                          ? t("products.form.fields.type_of_vigency_product")
                          : t("products.form.fields.type_of_vigency")}
                      </RequiredLabel>
                      <FormControl>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value || ""}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder=" " />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pluriannual">
                              {tk("vigency.pluriannual")}
                            </SelectItem>
                            <SelectItem value="annual">
                              {tk("vigency.annual")}
                            </SelectItem>
                            <SelectItem value="monthly">
                              {tk("vigency.monthly")}
                            </SelectItem>
                            <SelectItem value="daily">
                              {tk("vigency.daily")}
                            </SelectItem>
                            <SelectItem value="pay_per_use">
                              {tk("vigency.pay_per_use")}
                            </SelectItem>
                            <SelectItem value="lifetime">
                              {t("products.form.fields.lifetime")}
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {!assistanceSoulhubMode ? (
                  <>
                    <FormField
                      control={form.control}
                      name="iof_value"
                      render={({ field }) => (
                        <FormItem>
                          <RequiredLabel required>
                            {t("products.form.fields.iof_value")}
                          </RequiredLabel>
                          <FormControl>
                            <NumericInput
                              value={Number(field.value ?? 0)}
                              onChange={field.onChange}
                              variant="integer"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="susep_code"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            {t("products.form.fields.susep_code")}
                          </FormLabel>
                          <FormControl>
                            <Input {...field} value={field.value || ""} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </>
                ) : null}
              </div>

              <FormField
                control={form.control}
                name={`${cfgPrefix}.commercialization_rules` as any}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {tk("fields.commercialization_rules")}
                    </FormLabel>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {COMM_RULES.map((r) => (
                        <FormControl key={r}>
                          <label className="flex items-center gap-2 text-sm">
                            <Checkbox
                              checked={(field.value || []).includes(r)}
                              onCheckedChange={(checked) => {
                                const cur = field.value || [];
                                if (checked) {
                                  field.onChange([...cur, r]);
                                } else {
                                  field.onChange(
                                    cur.filter((x: string) => x !== r)
                                  );
                                  if (r === "vehicle_age") {
                                    form.setValue(
                                      `${cfgPrefix}.vehicle_age_rule` as any,
                                      undefined
                                    );
                                    form.setValue(
                                      `${cfgPrefix}.vehicle_age_only_min` as any,
                                      undefined
                                    );
                                    form.setValue(
                                      `${cfgPrefix}.vehicle_age_and_model_rules` as any,
                                      undefined
                                    );
                                  }
                                }
                              }}
                            />
                            {tk(`rules.${r}`)}
                          </label>
                        </FormControl>
                      ))}
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {rules.includes("age") ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="min_permitted_age"
                    render={({ field }) => (
                      <FormItem>
                        <RequiredLabel required>
                          {t("products.form.fields.min_permitted_age")}
                        </RequiredLabel>
                        <FormControl>
                          <NumericInput
                            value={Number(field.value ?? 0)}
                            onChange={field.onChange}
                            variant="integer"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="max_permitted_age"
                    render={({ field }) => (
                      <FormItem>
                        <RequiredLabel required>
                          {t("products.form.fields.max_permitted_age")}
                        </RequiredLabel>
                        <FormControl>
                          <NumericInput
                            value={Number(field.value ?? 0)}
                            onChange={field.onChange}
                            variant="integer"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              ) : null}

              {rules.includes("gender") ? (
                <FormField
                  control={form.control}
                  name={`${cfgPrefix}.gender` as any}
                  render={({ field }) => (
                    <FormItem>
                      <RequiredLabel required>
                        {tk("fields.gender")}
                      </RequiredLabel>
                      <FormControl>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value || ""}
                        >
                          <SelectTrigger className="w-full max-w-md">
                            <SelectValue placeholder=" " />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="feminine">
                              {tk("gender.feminine")}
                            </SelectItem>
                            <SelectItem value="masculine">
                              {tk("gender.masculine")}
                            </SelectItem>
                            <SelectItem value="lgbt">
                              {tk("gender.lgbt")}
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ) : null}

              {rules.includes("km") ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name={`${cfgPrefix}.km_min` as any}
                    render={({ field }) => (
                      <FormItem>
                        <RequiredLabel required>
                          {tk("fields.km_min")}
                        </RequiredLabel>
                        <FormControl>
                          <NumericInput
                            value={Number(field.value ?? 0)}
                            onChange={field.onChange}
                            variant="integer"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name={`${cfgPrefix}.km_max` as any}
                    render={({ field }) => (
                      <FormItem>
                        <RequiredLabel required>
                          {tk("fields.km_max")}
                        </RequiredLabel>
                        <FormControl>
                          <NumericInput
                            value={Number(field.value ?? 0)}
                            onChange={field.onChange}
                            variant="integer"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              ) : null}

              {(rules.includes("location_state") ||
                rules.includes("location_city")) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name={`${cfgPrefix}.location_mode` as any}
                    render={({ field }) => (
                      <FormItem>
                        <RequiredLabel required>
                          {tk("fields.location_mode")}
                        </RequiredLabel>
                        <FormControl>
                          <Select
                            onValueChange={field.onChange}
                            value={field.value || ""}
                          >
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder=" " />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="states">
                                {tk("location.states")}
                              </SelectItem>
                              <SelectItem value="cities">
                                {tk("location.cities")}
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name={`${cfgPrefix}.location_text` as any}
                    render={({ field }) => (
                      <FormItem>
                        <RequiredLabel required>
                          {tk("fields.location_text")}
                        </RequiredLabel>
                        <FormControl>
                          <Input {...field} value={field.value || ""} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              )}

              {rules.includes("vehicle_age") ? (
                <div className="space-y-4">
                  <FormField
                    control={form.control}
                    name={`${cfgPrefix}.vehicle_age_rule` as any}
                    render={({ field }) => (
                      <FormItem>
                        <RequiredLabel required>
                          {tk("fields.vehicle_age_rule")}
                        </RequiredLabel>
                        <FormControl>
                          <Select
                            onValueChange={field.onChange}
                            value={field.value || ""}
                          >
                            <SelectTrigger className="w-full max-w-lg">
                              <SelectValue placeholder=" " />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="age_only">
                                {tk("vehicle_age.age_only")}
                              </SelectItem>
                              <SelectItem value="age_and_model">
                                {tk("vehicle_age.age_and_model")}
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  {vehicleAgeRule === "age_only" ? (
                    <FormField
                      control={form.control}
                      name={`${cfgPrefix}.vehicle_age_only_min` as any}
                      render={({ field }) => (
                        <FormItem>
                          <RequiredLabel required>
                            {tk("fields.vehicle_age_only_min")}
                          </RequiredLabel>
                          <FormControl>
                            <NumericInput
                              value={Number(field.value ?? 0)}
                              onChange={field.onChange}
                              variant="integer"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  ) : null}
                  {vehicleAgeRule === "age_and_model" ? (
                    <FormField
                      control={form.control}
                      name={`${cfgPrefix}.vehicle_age_and_model_rules` as any}
                      render={({ field }) => (
                        <FormItem>
                          <RequiredLabel required>
                            {tk("fields.vehicle_age_and_model_rules")}
                          </RequiredLabel>
                          <FormControl>
                            <Input {...field} value={field.value || ""} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  ) : null}
                </div>
              ) : null}

              {(rules.includes("vehicle_type") ||
                rules.includes("model_or_brand_groups")) && (
                <FormField
                  control={form.control}
                  name={`${cfgPrefix}.vehicle_type_rules` as any}
                  render={({ field }) => (
                    <FormItem>
                      <RequiredLabel required>
                        {tk("fields.vehicle_type_rules")}
                      </RequiredLabel>
                      <FormControl>
                        <Input {...field} value={field.value || ""} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <RequiredLabel required>
                      {t("products.form.fields.description")}
                    </RequiredLabel>
                    <FormControl>
                      <RichTextEditor
                        value={field.value || ""}
                        onChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="insurance_coverages"
                render={({ field }) => (
                  <FormItem>
                    <RequiredLabel required>
                      {t("products.form.fields.coverage")}
                    </RequiredLabel>
                    <FormControl>
                      <RichTextEditor
                        value={field.value || ""}
                        onChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="excluded_risks"
                render={({ field }) => (
                  <FormItem>
                    <RequiredLabel required>
                      {tk("fields.excluded_items")}
                    </RequiredLabel>
                    <FormControl>
                      <RichTextEditor
                        value={field.value || ""}
                        onChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="general_conditions"
                render={({ field }) => (
                  <FormItem>
                    <RequiredLabel required>
                      {t("products.form.fields.general_conditions")}
                    </RequiredLabel>
                    <FormControl>
                      <RichTextEditor
                        value={field.value || ""}
                        onChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name={`${cfgPrefix}.premium_price_type` as any}
                render={({ field }) => (
                  <FormItem>
                    <RequiredLabel required>
                      {tk("fields.premium_price_type")}
                    </RequiredLabel>
                    <FormControl>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value || ""}
                      >
                        <SelectTrigger className="w-full max-w-md">
                          <SelectValue placeholder=" " />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="net">
                            {tk("premium.net")}
                          </SelectItem>
                          <SelectItem value="commissioned">
                            {tk("premium.commissioned")}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {(premiumType === "net" ||
                (premiumType === "commissioned" &&
                  markupEnabled === true &&
                  markupMode === "percentage")) ? (
                <FormField
                  control={form.control}
                  name={`${cfgPrefix}.net_price` as any}
                  render={({ field }) => (
                    <FormItem>
                      <RequiredLabel required>
                        {premiumType === "net"
                          ? tk("fields.net_price")
                          : tk("fields.net_price_markup_base")}
                      </RequiredLabel>
                      <FormControl>
                        <RealCurrencyInput
                          value={Number(field.value ?? 0)}
                          onChange={field.onChange}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ) : null}

              {premiumType === "commissioned" ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name={`${cfgPrefix}.commissioned_price` as any}
                    render={({ field }) => (
                      <FormItem>
                        <RequiredLabel required>
                          {tk("fields.commissioned_price")}
                        </RequiredLabel>
                        <FormControl>
                          <RealCurrencyInput
                            value={Number(field.value ?? 0)}
                            onChange={field.onChange}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name={`${cfgPrefix}.commission_percent` as any}
                    render={({ field }) => (
                      <FormItem>
                        <RequiredLabel required>
                          {tk("fields.commission_percent")}
                        </RequiredLabel>
                        <FormControl>
                          <NumericInput
                            value={Number(field.value ?? 0)}
                            onChange={field.onChange}
                            variant="integer"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              ) : null}

              {!(assistanceSoulhubMode && premiumType === "net") ? (
                <FormField
                  control={form.control}
                  name={`${cfgPrefix}.markup_enabled` as any}
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center gap-3 space-y-0">
                      <FormControl>
                        <Switch
                          checked={field.value === true}
                          onCheckedChange={(v) => {
                            field.onChange(v);
                            if (v === true) {
                              form.setValue(`${cfgPrefix}.markup_mode` as any, "percentage");
                            } else {
                              form.setValue(`${cfgPrefix}.markup_mode` as any, undefined);
                              form.setValue(`${cfgPrefix}.markup_value` as any, undefined);
                            }
                          }}
                        />
                      </FormControl>
                      <FormLabel>{tk("fields.markup_enabled")}</FormLabel>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ) : null}

              {showMarkupFields ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name={`${cfgPrefix}.markup_mode` as any}
                    render={({ field }) => (
                      <FormItem>
                        <RequiredLabel required>
                          {tk("fields.markup_mode")}
                        </RequiredLabel>
                        <FormControl>
                          <Select
                            onValueChange={field.onChange}
                            value={field.value || ""}
                          >
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder=" " />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="percentage">
                                {tk("markup.mode_percent_on_net")}
                              </SelectItem>
                              <SelectItem value="real_currency">
                                {tk("markup.mode_fixed_value")}
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name={`${cfgPrefix}.markup_value` as any}
                    render={({ field }) => (
                      <FormItem>
                        <RequiredLabel required>
                          {markupMode === "real_currency"
                            ? tk("fields.markup_value_currency")
                            : tk("fields.markup_value_percent")}
                        </RequiredLabel>
                        <FormControl>
                          {markupMode === "real_currency" ? (
                            <RealCurrencyInput
                              value={Number(field.value ?? 0)}
                              onChange={field.onChange}
                            />
                          ) : (
                            <div className="relative">
                              <Input
                                type="number"
                                min={0}
                                step={0.01}
                                className="pr-8"
                                value={
                                  field.value === undefined || field.value === null
                                    ? ""
                                    : String(field.value)
                                }
                                onChange={(e) => {
                                  const raw = e.target.value;
                                  field.onChange(
                                    raw === "" ? undefined : Number(raw),
                                  );
                                }}
                              />
                              <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                                %
                              </span>
                            </div>
                          )}
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              ) : null}

              <FormField
                control={form.control}
                name={`${cfgPrefix}.pro_labore_enabled` as any}
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center gap-3 space-y-0">
                    <FormControl>
                      <Switch
                        checked={field.value === true}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <FormLabel>
                      {tk("fields.pro_labore_enabled")}
                    </FormLabel>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {proLaboreOn ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name={`${cfgPrefix}.pro_labore_value_mode` as any}
                    render={({ field }) => (
                      <FormItem>
                        <RequiredLabel required>
                          {tk("fields.pro_labore_mode")}
                        </RequiredLabel>
                        <FormControl>
                          <Select
                            onValueChange={field.onChange}
                            value={field.value || ""}
                          >
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder=" " />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="real_currency">
                                {tk("pro_labore.real")}
                              </SelectItem>
                              <SelectItem value="percentage">
                                {tk("pro_labore.percent")}
                              </SelectItem>
                              <SelectItem value="custom_rule">
                                {tk("pro_labore.custom_rule")}
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  {proLaboreMode === "custom_rule" ? (
                    <FormField
                      control={form.control}
                      name={`${cfgPrefix}.pro_labore_custom_rule` as any}
                      render={({ field }) => (
                        <FormItem className="md:col-span-2">
                          <RequiredLabel required>
                            {tk("fields.pro_labore_custom_rule")}
                          </RequiredLabel>
                          <FormControl>
                            <Input {...field} value={field.value || ""} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  ) : (
                    <FormField
                      control={form.control}
                      name={`${cfgPrefix}.pro_labore_value` as any}
                      render={({ field }) => (
                        <FormItem>
                          <RequiredLabel required>
                            {tk("fields.pro_labore_value")}
                          </RequiredLabel>
                          <FormControl>
                            {proLaboreMode === "real_currency" ? (
                              <RealCurrencyInput
                                value={Number(field.value ?? 0)}
                                onChange={field.onChange}
                              />
                            ) : (
                              <NumericInput
                                value={Number(field.value ?? 0)}
                                onChange={field.onChange}
                                variant="integer"
                              />
                            )}
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}
                </div>
              ) : null}

              <FormField
                control={form.control}
                name={`${cfgPrefix}.has_additional_plans` as any}
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center gap-3 space-y-0">
                    <FormControl>
                      <Switch
                        checked={field.value === true}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <FormLabel>
                      {tk("fields.has_additional_plans")}
                    </FormLabel>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </Card>
          </TabsContent>

          <TabsContent value="plans">
            <Card className="p-4 space-y-4">
              {!hasPlans ? (
                <p className="text-sm text-muted-foreground">
                  {tk("plans.disabled_hint")}
                </p>
              ) : (
                <>
                  {planFields.map((pf, idx) => (
                    <Card key={pf.id} className="p-4 space-y-3 border-dashed">
                      <div className="flex justify-between items-center">
                        <span className="font-medium">
                          {tk("plans.plan_prefix")} {idx + 1}
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removePlan(idx)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                      {assistanceSoulhubMode ? (
                        <>
                          <FormField
                            control={form.control}
                            name={`capital_ranges.${idx}.plan_name` as any}
                            render={({ field }) => (
                              <FormItem>
                                <RequiredLabel required>
                                  {tk("plans.name")}
                                </RequiredLabel>
                                <FormControl>
                                  <Input {...field} value={field.value || ""} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name={`capital_ranges.${idx}.description` as any}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>
                                  {tk("plans.description")}
                                </FormLabel>
                                <FormControl>
                                  <Input {...field} value={field.value || ""} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name={`capital_ranges.${idx}.new_coverages` as any}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>
                                  {tk("plans.new_coverages")}
                                </FormLabel>
                                <FormControl>
                                  <Input {...field} value={field.value || ""} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <PlanAdjustmentsSection
                            form={form}
                            planIndex={idx}
                            parentField="capital_ranges"
                          />
                        </>
                      ) : (
                        <>
                          <FormField
                            control={form.control}
                            name={`${cfgPrefix}.plans.${idx}.name` as any}
                            render={({ field }) => (
                              <FormItem>
                                <RequiredLabel required>
                                  {tk("plans.name")}
                                </RequiredLabel>
                                <FormControl>
                                  <Input {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name={`${cfgPrefix}.plans.${idx}.description` as any}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>
                                  {tk("plans.description")}
                                </FormLabel>
                                <FormControl>
                                  <Input {...field} value={field.value || ""} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name={`${cfgPrefix}.plans.${idx}.new_coverages` as any}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>
                                  {tk("plans.new_coverages")}
                                </FormLabel>
                                <FormControl>
                                  <Input {...field} value={field.value || ""} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <PlanAdjustmentsBlock
                            form={form}
                            planIndex={idx}
                            cfgPrefix={cfgPrefix}
                            productKind={productKind}
                          />
                        </>
                      )}
                    </Card>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() =>
                      appendPlan(
                        assistanceSoulhubMode
                          ? createEmptySoulhubAssistanceCapitalRange()
                          : {
                              name: "",
                              description: "",
                              new_coverages: "",
                              adjustments: [],
                            },
                      )
                    }
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    {tk("plans.add")}
                  </Button>
                </>
              )}
            </Card>
          </TabsContent>
        </Tabs>

        <div className="flex gap-2 justify-end">
          <Button type="submit" className="w-full max-w-xs">
            {isEditMode
              ? tk("actions.update")
              : tk("actions.save")}
          </Button>
        </div>
      </form>
    </Form>
  );
};

const ADJUSTMENT_TYPES = [
  "lmi",
  "is",
  "ages",
  "usage_qty_in_term",
  "additional_assistance",
  "custom",
] as const;

const PlanAdjustmentsBlock: React.FC<{
  form: UseFormReturn<TProductFormData>;
  planIndex: number;
  cfgPrefix: "warranty_product_config" | "theft_product_config";
  productKind: "warranty" | "theft";
}> = ({ form, planIndex, cfgPrefix, productKind }) => {
  const { t } = useI18n();
  const tk = React.useCallback(
    (key: string) =>
      t(`products.${productKind === "theft" ? "theft" : "warranty"}.${key}`),
    [t, productKind],
  );
  const {
    fields,
    append,
    remove,
  } = useFieldArray({
    control: form.control,
    name: `${cfgPrefix}.plans.${planIndex}.adjustments` as any,
  });

  return (
    <div className="space-y-2">
      <FormLabel>{tk("plans.adjustments")}</FormLabel>
      {fields.map((f, ai) => (
        <div
          key={f.id}
          className="grid grid-cols-1 md:grid-cols-2 gap-2 border rounded p-2"
        >
          <FormField
            control={form.control}
            name={
              `${cfgPrefix}.plans.${planIndex}.adjustments.${ai}.adjustment_type` as any
            }
            render={({ field }) => (
              <FormItem>
                <FormLabel>{tk("plans.adjustment_type")}</FormLabel>
                <FormControl>
                  <Select onValueChange={field.onChange} value={field.value || ""}>
                    <SelectTrigger>
                      <SelectValue placeholder=" " />
                    </SelectTrigger>
                    <SelectContent>
                      {ADJUSTMENT_TYPES.map((opt) => (
                        <SelectItem key={opt} value={opt}>
                          {tk(`adjustment.${opt}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="flex items-end justify-end">
            <Button type="button" variant="ghost" size="sm" onClick={() => remove(ai)}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
          <FormField
            control={form.control}
            name={
              `${cfgPrefix}.plans.${planIndex}.adjustments.${ai}.new_assistance_value_applies` as any
            }
            render={({ field }) => (
              <FormItem className="flex items-center gap-2">
                <FormControl>
                  <Switch
                    checked={field.value === true}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <FormLabel className="!mt-0">
                  {tk("plans.new_assistance_value")}
                </FormLabel>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={
              `${cfgPrefix}.plans.${planIndex}.adjustments.${ai}.new_assistance_value` as any
            }
            render={({ field }) => (
              <FormItem>
                <FormLabel>{tk("plans.assistance_value_amount")}</FormLabel>
                <FormControl>
                  <RealCurrencyInput
                    value={Number(field.value ?? 0)}
                    onChange={field.onChange}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={
              `${cfgPrefix}.plans.${planIndex}.adjustments.${ai}.new_commission_applies` as any
            }
            render={({ field }) => (
              <FormItem className="flex items-center gap-2">
                <FormControl>
                  <Switch
                    checked={field.value === true}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <FormLabel className="!mt-0">
                  {tk("plans.new_commission")}
                </FormLabel>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={
              `${cfgPrefix}.plans.${planIndex}.adjustments.${ai}.new_commission_percent` as any
            }
            render={({ field }) => (
              <FormItem>
                <FormLabel>{tk("plans.commission_percent")}</FormLabel>
                <FormControl>
                  <NumericInput
                    value={Number(field.value ?? 0)}
                    onChange={field.onChange}
                    variant="integer"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={
              `${cfgPrefix}.plans.${planIndex}.adjustments.${ai}.additional_assistance_description` as any
            }
            render={({ field }) => (
              <FormItem className="md:col-span-2">
                <FormLabel>
                  {tk("plans.additional_assistance_text")}
                </FormLabel>
                <FormControl>
                  <Input {...field} value={field.value || ""} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      ))}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() =>
          append({
            adjustment_type: "lmi",
            new_assistance_value_applies: false,
            new_commission_applies: false,
          })
        }
      >
        <Plus className="h-4 w-4 mr-1" />
        {tk("plans.add_adjustment")}
      </Button>
    </div>
  );
};
